import api from './api';

// Card photos come back as a path like "/uploads/cards/xyz.jpg" (served
// by the backend's own StaticFiles mount, see main.py) - not under /api,
// so it needs the backend's bare origin, not the api.js baseURL (which
// includes the /api suffix).
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');

export const mediaUrl = (path) => (path ? `${API_ORIGIN}${path}` : null);

// --- Card scanner -----------------------------------------------------
// Usually finishes inside this one request (the fast path); if Groq's
// rate limit is hit the job comes back status:'queued' instead of an
// error - see app/routes/crm_routes.py's scan_card. Poll getScanJob(id)
// (or watch listScanJobs) until it's 'done'/'failed'.
export const scanCard = (file, eventId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (eventId) formData.append('event_id', eventId);
  // No explicit Content-Type - see api.js's 2026-09-13 comment on why
  // setting it here breaks the multipart boundary axios/the browser add
  // automatically for a FormData body.
  return api.post('/crm/cards/scan', formData);
};

// Multi-card version (spec §6) - one photo of several cards spread out.
// job.result is {"cards": [{...fields, confidence, confirmed}, ...]}
// instead of a single set of fields - see app/routes/crm_routes.py's
// scan_cards_multi. Polled/read the same way via getScanJob.
export const scanCardsMulti = (file, eventId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (eventId) formData.append('event_id', eventId);
  return api.post('/crm/cards/scan-multi', formData);
};

export const getScanJob = (jobId) => api.get(`/crm/cards/scan-jobs/${jobId}`);

// mineOnly=false shows the whole shared queue (any Sales/CEO user), not
// just the caller's own uploads - a booth scan might be reviewed by
// whoever's free, not necessarily who uploaded it.
export const listScanJobs = (mineOnly = true) =>
  api.get('/crm/cards/scan-jobs', { params: { mine_only: mineOnly } });

// First call omits duplicateDecision/matchedContactId - if the backend
// finds a likely-existing contact it responds {status:'needs_decision',
// duplicate:{...}} instead of saving. Re-call with the user's choice
// ('merge' + the matched contact's id, or 'keep_separate') to finish.
export const confirmCard = (payload) => api.post('/crm/cards/confirm', payload);

// --- Events (ad hoc - no pre-loaded calendar) --------------------------
export const listEvents = () => api.get('/crm/events');

export const createEvent = (payload) => api.post('/crm/events', payload);

// --- Accounts -----------------------------------------------------------
// Unscoped by owner - powers the card-scan confirm screen's "match into an
// existing account" picker (see crm_routes.py).
export const searchAccounts = (q) => api.get('/crm/accounts/search', { params: { q } });

// filters: { q, country, owner_id, event_id }
export const listAccounts = (filters = {}) => api.get('/crm/accounts', { params: filters });

export const getAccount = (accountId) => api.get(`/crm/accounts/${accountId}`);

// CEO-only (see app/routes/crm_routes.py's require_ceo_action). Same filters as
// listAccounts, but with no row cap - one CSV row per contact. Returns a blob;
// hand it to downloadBlob (utils/downloadFile.js).
export const exportAccountsCsv = (filters = {}) =>
  api.get('/crm/accounts/export', { params: filters, responseType: 'blob' });

// CEO-only. Fills missing country/state/city on existing accounts from their stored
// card addresses (blanks only - never overwrites). Parses at most ~40 addresses with
// AI per call, so if still_unparsed > 0 just call it again. Response: { countries_filled,
// cities_filled, countries_from_place, countries_by_ai, misplaced_fixed, no_address_on_file,
// still_unparsed, country_choices_left, rate_limited, no_country_left }.
export const backfillAccountLocations = () => api.post('/crm/accounts/backfill-locations');

export const createAccount = (payload) => api.post('/crm/accounts', payload);

export const updateAccount = (accountId, payload) => api.put(`/crm/accounts/${accountId}`, payload);

// Permanent, CEO-only (see app/routes/crm_routes.py's require_ceo) - deletes
// the account and everything under it (contacts, deals, activity, reminders).
export const deleteAccount = (accountId) => api.delete(`/crm/accounts/${accountId}`);

export const addContact = (accountId, payload) => api.post(`/crm/accounts/${accountId}/contacts`, payload);

// Fixes a scan mistake after the fact (e.g. a misread email) - same
// owner-or-CEO access as the account itself.
export const updateContact = (accountId, contactId, payload) => api.put(`/crm/accounts/${accountId}/contacts/${contactId}`, payload);

export const deleteContact = (accountId, contactId) => api.delete(`/crm/accounts/${accountId}/contacts/${contactId}`);

// --- Deals (the pipeline) ------------------------------------------------
// filters: { stage, owner_id, account_id, event_id }
export const listDeals = (filters = {}) => api.get('/crm/deals', { params: filters });

export const createDeal = (payload) => api.post('/crm/deals', payload);

export const updateDeal = (dealId, payload) => api.put(`/crm/deals/${dealId}`, payload);

// --- Products (a lightweight catalog, not a price book) -------------------
export const listProducts = () => api.get('/crm/products');

export const createProduct = (payload) => api.post('/crm/products', payload);

// --- Activity log + reminders (per account or per deal) -------------------
export const logActivity = (payload) => api.post('/crm/activity', payload);

export const createReminder = (payload) => api.post('/crm/reminders', payload);

export const completeReminder = (reminderId) => api.put(`/crm/reminders/${reminderId}/complete`);

// --- Team (Sales Department + CEO - for owner filter/reassignment) --------
export const listTeam = () => api.get('/crm/team');

// --- Dashboard ------------------------------------------------------------
export const getDashboard = () => api.get('/crm/dashboard');

// Prospect counts by region / country / city for the dashboard's charts and world
// map (every account counts as a prospect). Same owner scope as getDashboard.
// Response: { total, regions:[{region,count}], countries:[{country,code,numeric,region,count}],
// cities:[{city,country,code,lat,lng,approx,count}], gaps:{no_country,no_city,unplaced:[...]} }.
export const getDashboardGeography = () => api.get('/crm/dashboard/geography');

// --- CSV import of existing contacts ---------------------------------------
// Row-level partial success - see app/routes/crm_routes.py's import_contacts
// docstring. Response: { rows_total, contacts_created, accounts_created,
// duplicates_flagged, errors: [{row, message}] }.
export const importContacts = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/crm/contacts/import', formData);
};

// Downloads the blank template - the caller triggers the browser's save
// dialog itself (see ImportContactsPage.js), api.js already returns JSON
// by default so this asks for a blob response instead.
export const downloadContactsImportTemplate = () =>
  api.get('/crm/contacts/import-template', { responseType: 'blob' });

// --- AI-drafted emails (drafts only - nothing is sent) --------------------
// The type picker's options - {key, label} (e.g. "introductory",
// "meeting_request"...), sourced from the backend's EMAIL_TYPES so the
// list can't drift out of sync with what the picker actually offers.
export const listEmailTypes = () => api.get('/crm/accounts/email-drafts/types');

// One draft per account, each attempted independently (partial success -
// see app/routes/crm_routes.py's draft_intro_emails). emailType is one of
// listEmailTypes()'s keys; useLocalSalutation opens/closes each draft in
// the recipient's language when their account's country has one (see
// app/crm/salutations.py) - the body itself always stays in English.
// Response: { drafts: [{account_id, account_name, contact_name,
// contact_email, subject, body, language, error}] }.
export const draftIntroEmails = (accountIds, emailType, useLocalSalutation = true) =>
  api.post('/crm/accounts/email-drafts', {
    account_ids: accountIds, email_type: emailType, use_local_salutation: useLocalSalutation,
  });

// --- Selecting customers by criteria + bulk invitation emails --------------
// The Customers list, the CSV export, "select all matching" and bulk email all take
// the same filters: { q, country, owner_id, event_id, missing, region, state, city,
// product_id, stage, quiet_days, has_email } (see account_filter_params in crm_routes.py).

// Every matching id, not just the list's first 200. { ids, count, truncated }.
export const getAccountIds = (filters = {}) => api.get('/crm/accounts/ids', { params: filters });

// What the filter dropdowns offer: { countries, regions, states, cities, stages }.
export const getAccountFilterOptions = () => api.get('/crm/accounts/filter-options');

export const listBulkEmailTypes = () => api.get('/crm/accounts/bulk-email/types');

// { imap_configured, can_save, mailbox } - can_save is CEO-only.
export const getBulkEmailStatus = () => api.get('/crm/accounts/bulk-email/status');

// mode: 'primary' (one contact per company) | 'all' (every contact with an email).
// -> { recipients: [...], skipped: [{ account_id, account_name, reason }] }
export const bulkEmailRecipients = (accountIds, mode = 'primary') =>
  api.post('/crm/accounts/bulk-email/recipients', { account_ids: accountIds, mode });

// payload: { recipients: [{account_id, contact_id}], email_type, event, use_local_salutation, personalize }
// -> { mode: 'merge' | 'individual', drafts: [...] }. One AI call in merge mode, so it can take a few seconds.
export const bulkEmailDraft = (payload) => api.post('/crm/accounts/bulk-email/draft', payload);

const deliveryForm = (drafts, files) => {
  const form = new FormData();
  form.append('drafts', JSON.stringify(drafts));
  files.forEach((file) => form.append('files', file));
  return form;
};

// A zip of .eml files (blob) - hand it to downloadBlob. Nothing is sent.
export const bulkEmailDownload = (drafts, files = []) =>
  api.post('/crm/accounts/bulk-email/download', deliveryForm(drafts, files), {
    responseType: 'blob', headers: { 'Content-Type': 'multipart/form-data' },
  });

// CEO only - saves into the configured Zoho mailbox's Drafts folder. -> { mailbox, saved, results }
export const bulkEmailSaveDrafts = (drafts, files = []) =>
  api.post('/crm/accounts/bulk-email/save-drafts', deliveryForm(drafts, files), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
